import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { MediaCard } from "../components/MediaCard";
import { FollowersModal } from "../components/FollowersModal";
import { UserAvatar } from "../components/UserAvatar";
import { User, Edit, Check, X, BookOpen, Star, Clock, ListTodo, XCircle, ExternalLink, Sun, Moon, Monitor, Camera, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useTheme } from "../components/ThemeProvider";
import { useTranslation } from "../i18n";

const API = '/api';

function ThemeSelector() {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const options = [
    { value: "light", label: t.profile.themeLight, icon: Sun },
    { value: "dark", label: t.profile.themeDark, icon: Moon },
    { value: "system", label: t.profile.themeSystem, icon: Monitor },
  ];
  return (
    <div>
      <Label className="text-sm text-muted-foreground mb-3 block">{t.profile.themeLabel}</Label>
      <div className="flex gap-2">
        {options.map(({ value, label, icon: Icon }) => (
          <Button
            key={value}
            variant={theme === value ? "default" : "outline"}
            size="sm"
            onClick={() => setTheme(value)}
            className="gap-2"
            data-testid={`theme-${value}`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function Profile({ user, setUser }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(user?.name || "");
  const [editBio, setEditBio] = useState(user?.bio || "");
  const [editUsername, setEditUsername] = useState(user?.username || "");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [libraryEntries, setLibraryEntries] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [followersOpen, setFollowersOpen] = useState(false);
  const [followingOpen, setFollowingOpen] = useState(false);
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    setEditName(user?.name || "");
    setEditBio(user?.bio || "");
    setEditUsername(user?.username || "");
    fetchData();
  }, [user]);

  useEffect(() => { fetchLibrary(); }, [selectedStatus]);
  
  const fetchData = async () => {
    try {
      const [statsRes, libraryRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/library?sort_by=updated_at&sort_order=desc`),
      ]);
      setStats(statsRes.data);
      setLibraryEntries(libraryRes.data.entries);
      // Fetch follower counts if user has username
      if (user?.username) {
        try {
          const profileRes = await axios.get(`${API}/profile/${user.username}`);
          setFollowersCount(profileRes.data.followers_count);
          setFollowingCount(profileRes.data.following_count);
        } catch {}
      }
    } catch (error) { console.error("Error:", error); }
    finally { setLoading(false); }
  };

  const fetchLibrary = async () => {
    try {
      const params = selectedStatus !== "all" ? `?status=${selectedStatus}&sort_by=updated_at&sort_order=desc` : "?sort_by=updated_at&sort_order=desc";
      const response = await axios.get(`${API}/library${params}`);
      setLibraryEntries(response.data.entries);
    } catch (error) { console.error("Error:", error); }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { name: editName, bio: editBio };
      if (editUsername !== user?.username) payload.username = editUsername;
      const response = await axios.put(`${API}/users/me`, payload);
      if (setUser) setUser(response.data);
      setEditing(false);
      toast.success(t.profile.profileUpdated);
    } catch (error) {
      toast.error(error.response?.data?.detail || t.profile.updateError);
    }
    finally { setSaving(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Formato no soportado. Usa JPG, PNG o WebP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no puede superar 5 MB.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/users/me/photo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (setUser) setUser(response.data);
      toast.success(t.profile.avatarUpdated);
    } catch (error) {
      toast.error(error.response?.data?.detail || t.profile.avatarError);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const statusLabel = (s) => {
    const labels = { in_progress: t.statuses.in_progress.toLowerCase(), planned: t.statuses.planned.toLowerCase(), completed: t.statuses.completed.toLowerCase(), dropped: t.statuses.dropped.toLowerCase() };
    return labels[s] || s;
  };
  
  if (loading) return (<div className="flex items-center justify-center min-h-[50vh]"><div className="animate-pulse text-muted-foreground">{t.profile.loadingProfile}</div></div>);
  
  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="profile-page">
      <Card>
        <CardContent className="p-4 md:pt-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 md:gap-6">
            <div className="relative group">
              <UserAvatar user={user} size="xl" className="w-20 h-20 md:w-24 md:h-24" />
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="photo-upload-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                data-testid="upload-photo-button"
              >
                {uploading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
              </button>
            </div>
            <div className="flex-1 text-center sm:text-left">
              {editing ? (
                <div className="space-y-3 max-w-md">
                  <div><Label htmlFor="username">{t.social.username}</Label><Input id="username" value={editUsername} onChange={(e) => setEditUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} placeholder={t.social.usernamePlaceholder} data-testid="edit-username-input" /></div>
                  <div><Label htmlFor="name">{t.profile.name}</Label><Input id="name" value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-name-input" /></div>
                  <div><Label htmlFor="bio">{t.profile.bio}</Label><Textarea id="bio" value={editBio} onChange={(e) => setEditBio(e.target.value)} placeholder={t.profile.bioPlaceholder} rows={3} data-testid="edit-bio-input" /></div>
                  <div className="flex gap-2">
                    <Button onClick={handleSave} disabled={saving} data-testid="save-profile"><Check className="w-4 h-4 mr-2" />{saving ? t.common.saving : t.common.save}</Button>
                    <Button variant="ghost" onClick={() => setEditing(false)}><X className="w-4 h-4 mr-2" />{t.common.cancel}</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 justify-center sm:justify-start flex-wrap">
                    <h1 className="text-2xl font-bold" data-testid="profile-name">@{user?.username || t.profile.anonymous}</h1>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="edit-profile"><Edit className="w-4 h-4" /></Button>
                    {user?.username && (
                      <Button variant="outline" size="sm" asChild data-testid="view-public-profile">
                        <Link to={`/profile/${user.username}`}><ExternalLink className="w-3 h-3 mr-1.5" />{t.social.viewProfile}</Link>
                      </Button>
                    )}
                  </div>
                  <p className="text-muted-foreground mt-1 text-sm" data-testid="profile-email">{user?.email}</p>
                  {user?.bio && <p className="mt-2 text-sm" data-testid="profile-bio">{user.bio}</p>}
                  <div className="flex items-center gap-4 mt-3 justify-center sm:justify-start">
                    <button onClick={() => setFollowersOpen(true)} className="text-sm hover:underline" data-testid="my-followers-count">
                      <span className="font-bold">{followersCount}</span> <span className="text-muted-foreground">{t.social.followers}</span>
                    </button>
                    <button onClick={() => setFollowingOpen(true)} className="text-sm hover:underline" data-testid="my-following-count">
                      <span className="font-bold">{followingCount}</span> <span className="text-muted-foreground">{t.social.following}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
            <Card className="card-hover cursor-pointer h-full" onClick={() => setSelectedStatus("all")}>
              <CardContent className="p-4 md:pt-6 text-center">
                <BookOpen className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-1.5 md:mb-2 text-primary" />
                <div className="text-xl md:text-2xl font-bold">{stats.total_items}</div>
                <p className="text-xs md:text-sm text-muted-foreground">{t.profile.total}</p>
              </CardContent>
            </Card>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="card-hover cursor-pointer h-full" onClick={() => setSelectedStatus("completed")}>
              <CardContent className="p-4 md:pt-6 text-center">
                <div className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-1.5 md:mb-2 rounded-full bg-green-500 flex items-center justify-center"><Check className="w-3 h-3 md:w-4 md:h-4 text-white" /></div>
                <div className="text-xl md:text-2xl font-bold">{stats.by_status?.completed || 0}</div>
                <p className="text-xs md:text-sm text-muted-foreground">{t.profile.done}</p>
              </CardContent>
            </Card>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="card-hover cursor-pointer h-full" onClick={() => setSelectedStatus("in_progress")}>
              <CardContent className="p-4 md:pt-6 text-center">
                <div className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-1.5 md:mb-2 rounded-full bg-blue-500 flex items-center justify-center"><Clock className="w-3 h-3 md:w-4 md:h-4 text-white" /></div>
                <div className="text-xl md:text-2xl font-bold">{stats.by_status?.in_progress || 0}</div>
                <p className="text-xs md:text-sm text-muted-foreground">{t.profile.active}</p>
              </CardContent>
            </Card>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="card-hover h-full">
              <CardContent className="p-4 md:pt-6 text-center">
                <Star className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-1.5 md:mb-2 fill-yellow-400 text-yellow-400" />
                <div className="text-xl md:text-2xl font-bold">{stats.overall_avg_rating?.toFixed(1) || "—"}</div>
                <p className="text-xs md:text-sm text-muted-foreground">{t.profile.rating}</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}
      
      {stats?.by_media_type?.length > 0 && (
        <Card>
          <CardHeader className="px-4 md:px-6"><CardTitle className="text-sm md:text-base font-medium">{t.profile.collectionBreakdown}</CardTitle></CardHeader>
          <CardContent className="px-4 md:px-6">
            <div className="space-y-2 md:space-y-3">
              {stats.by_media_type.map((item) => (
                <div key={item._id} className="flex items-center justify-between text-sm">
                  <span>{t.mediaTypesPlural[item._id] || item._id?.replace("_", " ") || "—"}</span>
                  <div className="flex items-center gap-2 md:gap-3">
                    <span className="font-mono">{item.count}</span>
                    {item.avg_rating && <span className="text-xs text-muted-foreground">★ {item.avg_rating.toFixed(1)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      <div>
        <h2 className="text-base md:text-xl font-semibold mb-3 md:mb-4">{t.profile.myCollection}</h2>
        <Tabs value={selectedStatus} onValueChange={setSelectedStatus} className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-3 md:mb-4 h-auto" data-testid="profile-status-tabs">
            <TabsTrigger value="all" className="text-xs md:text-sm py-2.5 min-h-[44px]">{t.profile.all}</TabsTrigger>
            <TabsTrigger value="in_progress" className="text-xs md:text-sm py-2.5 min-h-[44px]"><Clock className="w-3 h-3 mr-0.5 hidden sm:block" /><span className="sm:hidden">{t.profile.active}</span><span className="hidden sm:inline">{t.statuses.in_progress}</span></TabsTrigger>
            <TabsTrigger value="planned" className="text-xs md:text-sm py-2.5 min-h-[44px]">{t.statuses.planned}</TabsTrigger>
            <TabsTrigger value="completed" className="text-xs md:text-sm py-2.5 min-h-[44px]"><Check className="w-3 h-3 mr-0.5 hidden sm:block" /><span className="sm:hidden">{t.profile.done}</span><span className="hidden sm:inline">{t.statuses.completed}</span></TabsTrigger>
            <TabsTrigger value="dropped" className="text-xs md:text-sm py-2.5 min-h-[44px]">{t.statuses.dropped}</TabsTrigger>
          </TabsList>
          <TabsContent value={selectedStatus} className="mt-0">
            {libraryEntries.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4 lg:grid-cols-6">
                {libraryEntries.slice(0, 12).map((entry, index) => (<MediaCard key={entry.entry_id} entry={entry} showStatus index={index} />))}
              </div>
            ) : (
              <Card className="text-center py-6 md:py-8"><CardContent><p className="text-sm text-muted-foreground">{selectedStatus === "all" ? t.profile.noItemsYet : t.profile.noStatusItems(statusLabel(selectedStatus))}</p>{selectedStatus === "all" && <Button asChild className="mt-4 min-h-[44px]"><Link to="/search">{t.common.browsMedia}</Link></Button>}</CardContent></Card>
            )}
            {libraryEntries.length > 12 && (
              <div className="mt-3 md:mt-4 text-center"><Button variant="outline" asChild className="min-h-[44px]"><Link to={`/library${selectedStatus !== "all" ? `?status=${selectedStatus}` : ""}`}>{t.profile.viewAllItems(libraryEntries.length)}</Link></Button></div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Preferences Section */}
      <Card className="mt-6">
        <CardHeader><CardTitle className="text-base">{t.profile.preferences}</CardTitle></CardHeader>
        <CardContent>
          <ThemeSelector />
        </CardContent>
      </Card>

      {user?.username && (
        <>
          <FollowersModal open={followersOpen} onOpenChange={setFollowersOpen} username={user.username} mode="followers" displayName={user.name} />
          <FollowersModal open={followingOpen} onOpenChange={setFollowingOpen} username={user.username} mode="following" displayName={user.name} />
        </>
      )}
    </div>
  );
}

export default Profile;
